import { z } from 'zod';
import { COMMAND_PAYLOADS } from './commands.js';
import { Id, TimestampMs } from './common.js';
import { HistoryImportParams } from './history.js';
import { VikoHandoffInput, VikoPostArtifactInput, VikoSearchHistoryInput, VikoTaskCreateInput, VikoTaskUpdateInput, } from './mcp.js';
import { VikoTaskCreateBatchInput } from './plan-batch.js'; // g18-batch
import { WebAuthnAssertion } from './signing.js';
/**
 * Additive (v0) work-model frames: standing dispatch grants + hub dispatch, hygiene
 * reports, work-model change notifications for clients, the ranked inbox, and the params of the
 * daemon → hub `rpc` methods. See docs/protocol.md "Work model frames" and docs/signing.md
 * Appendix A. Peers that predate these frames ignore them.
 */
const f = (type, shape) => z.object({ type: z.literal(type), ...shape });
// ---------------------------------------------------------------------------------------------
// Work model records
// ---------------------------------------------------------------------------------------------
/** Hub task status (MCP `in_progress` ≙ `running`, `dropped` ≙ `cancelled`). */
export const WorkTaskStatus = z.enum([
    'proposed',
    'ready',
    'running',
    'blocked',
    'review',
    'landing',
    'done',
    'cancelled',
]);
export const Provenance = z.enum(['user', 'agent', 'brain']);
export const WorkEntity = z.enum([
    'project',
    'idea',
    'workstream',
    'task',
    'rule',
    'landing',
    'build',
    'artifact',
    'grant',
    'card',
    'machine',
]);
/** hub → client: a work-model record changed. `data` is the record as the REST API returns it. */
export const WorkFrame = f('work', {
    entity: WorkEntity,
    op: z.enum(['upsert', 'delete']),
    id: Id,
    data: z.unknown().optional(),
});
/**
 * hub → client: the ranked Inbox (`@viko/scheduler` rankInbox): decision sessions per
 * workstream, digest items and snoozed items. Sent on every change next to the legacy `inbox`.
 */
export const InboxRankedFrame = f('inbox.ranked', {
    sessions: z.array(z.object({
        id: z.string(),
        projectId: z.string(),
        workstreamId: z.string().optional(),
        leverage: z.number(),
        oldestAt: TimestampMs,
        items: z.array(z.object({ item: z.looseObject({ id: z.string() }), leverage: z.number() })),
    })),
    digest: z.array(z.looseObject({ id: z.string() })),
    snoozed: z.array(z.looseObject({ id: z.string() })),
});
// ---------------------------------------------------------------------------------------------
// Standing dispatch grants (docs/signing.md Appendix A). Mirrors @viko/crypto grant.ts.
// ---------------------------------------------------------------------------------------------
export const DispatchKind = z.enum(['run.start', 'run.send', 'run.stop']);
export const DispatchGrantStatement = z.object({
    v: z.literal(1),
    type: z.literal('viko.dispatch-grant'),
    grantId: Id,
    machineIds: z.array(Id).min(1),
    projectIds: z.array(Id).min(1),
    workstreamIds: z.array(Id).min(1).optional(),
    kinds: z.array(DispatchKind).min(1),
    limits: z.object({
        maxStartsPerHour: z.number().int().positive(),
        maxConcurrentRuns: z.number().int().positive(),
        maxPromptBytes: z.number().int().positive(),
    }),
    deviceKeyId: Id,
    nonce: z.string().min(16),
    issuedAt: TimestampMs,
    expiresAt: TimestampMs,
});
export const DispatchGrant = z.object({
    statement: DispatchGrantStatement,
    /** Device key signature (raw r||s, base64url) under `viko/dispatch-grant/v1`. */
    signature: z.string().min(1),
    /** Passkey assertion over SHA-256(`viko/dispatch-grant/v1` ‖ 0 ‖ JCS(statement)). */
    assertion: WebAuthnAssertion,
});
export const GrantRevocation = z.object({
    statement: z.object({
        v: z.literal(1),
        type: z.literal('viko.dispatch-grant-revocation'),
        grantIds: z.array(Id).min(1),
        deviceKeyId: Id,
        nonce: z.string().min(16),
        issuedAt: TimestampMs,
    }),
    signature: z.string().min(1),
});
/** hub → daemon: a stored grant (after `welcome`, and when a new one is added). */
export const PolicyGrantFrame = f('policy.grant', { grant: DispatchGrant });
/** hub → daemon: device-signed revocation of one or more grants. */
export const PolicyRevokeAllFrame = f('policy.revoke-all', {
    grantIds: z.array(z.string()).optional(),
});
export const PolicyRevokeFrame = f('policy.revoke', { revocation: GrantRevocation });
/**
 * hub → daemon: the router asks the daemon to act under a grant. The daemon verifies the grant
 * and `checkDispatch` before acting; `dispatchId` is the idempotency key.
 */
export const HubDispatchFrame = f('hub.dispatch', {
    dispatchId: Id,
    grantId: Id,
    kind: DispatchKind,
    projectId: Id,
    workstreamId: z.string().optional(),
    taskId: z.string().optional(),
    /** Run to start (`run.start`), feed (`run.send`) or stop (`run.stop`). */
    runId: Id,
    /**
     * `run.start`: COMMAND_PAYLOADS['run.start'] (projectId/taskId/runId must match the frame);
     * `run.send`: COMMAND_PAYLOADS['run.message']; `run.stop`: COMMAND_PAYLOADS['run.stop'].
     */
    payload: z.unknown(),
    issuedAt: TimestampMs,
    expiresAt: TimestampMs,
    /** Router explanation for the audit log. */
    reason: z.string().optional(),
    /**
     * Additive: `run.stop` from a session cap (compaction / max age) — rotate instead of just
     * stopping: the daemon starts a fresh run on the same workstream with the brief.
     */
    rotate: z.boolean().optional(),
});
export const DispatchRejectReason = z.enum([
    'no-grant',
    'bad-grant',
    'grant-expired',
    'grant-revoked',
    'machine-out-of-scope',
    'project-out-of-scope',
    'workstream-out-of-scope',
    'kind-not-allowed',
    'rate-limited',
    'concurrency-limit',
    'prompt-too-large',
    'run-out-of-scope',
    'payload-mismatch',
    'invalid-payload',
    'expired',
]);
/** daemon → hub: outcome of a `hub.dispatch`. */
export const HubDispatchResultFrame = f('hub.dispatch.result', {
    dispatchId: Id,
    status: z.enum(['accepted', 'duplicate', 'rejected', 'done', 'failed']),
    reason: DispatchRejectReason.optional(),
    error: z.string().optional(),
    result: z.unknown().optional(),
});
/** Max validity of one `hub.dispatch` frame (the grant itself lives longer). */
export const MAX_DISPATCH_TTL_MS = 10 * 60_000;
export const DISPATCH_PAYLOADS = {
    'run.start': COMMAND_PAYLOADS['run.start'],
    'run.send': COMMAND_PAYLOADS['run.message'],
    'run.stop': COMMAND_PAYLOADS['run.stop'],
};
const utf8Len = (s) => new TextEncoder().encode(s).length;
/**
 * Pure scope check of a dispatch against an already signature-verified grant statement.
 * Order: grant revoked/expired → dispatch validity → machine → project → workstream → kind → payload
 * shape/consistency → run scope → prompt size → concurrency → rate.
 */
export function checkDispatch(grant, d, st) {
    const no = (reason, detail) => detail === undefined ? { ok: false, reason } : { ok: false, reason, detail };
    if (d.grantId !== grant.grantId)
        return no('no-grant');
    if (st.revokedGrantIds.has(grant.grantId))
        return no('grant-revoked');
    if (st.now > grant.expiresAt || st.now < grant.issuedAt - 60_000)
        return no('grant-expired');
    if (d.expiresAt - d.issuedAt > MAX_DISPATCH_TTL_MS || st.now > d.expiresAt)
        return no('expired');
    if (!grant.machineIds.includes(st.machineId))
        return no('machine-out-of-scope');
    if (!grant.projectIds.includes(d.projectId))
        return no('project-out-of-scope');
    if (grant.workstreamIds && (!d.workstreamId || !grant.workstreamIds.includes(d.workstreamId))) {
        return no('workstream-out-of-scope');
    }
    if (!grant.kinds.includes(d.kind))
        return no('kind-not-allowed');
    const parsed = DISPATCH_PAYLOADS[d.kind].safeParse(d.payload);
    if (!parsed.success)
        return no('invalid-payload', parsed.error.message);
    const p = parsed.data;
    if (p.runId !== d.runId)
        return no('payload-mismatch', 'runId');
    let text = '';
    if (d.kind === 'run.start') {
        if (p.projectId !== d.projectId)
            return no('payload-mismatch', 'projectId');
        if ((p.workstreamId ?? undefined) !== d.workstreamId) {
            return no('payload-mismatch', 'workstreamId');
        }
        if (!d.taskId || p.taskId !== d.taskId)
            return no('payload-mismatch', 'taskId');
        // Existing passkey-bound grants authorize only the daemon's trusted default identity.
        // Even an explicit default ID is forbidden: adding a payload selector must not expand
        // old grants. Account selection requires a directly signed user command.
        if (p.accountId !== undefined)
            return no('payload-mismatch', 'accountId is not allowed under a grant');
        if (p.privateProject !== undefined)
            return no('payload-mismatch', 'privateProject requires a directly signed user command');
        // A grant never resumes arbitrary native sessions or elevates the permission mode.
        if (p.resumeNativeSessionId !== undefined || p.mode !== undefined || p.cwd !== undefined) {
            return no('payload-mismatch', 'resume/mode/cwd are not allowed under a grant');
        }
        text = String(p.prompt ?? '');
    }
    else {
        if (!st.run)
            return no('run-out-of-scope', 'unknown run');
        if (st.run.projectId !== d.projectId)
            return no('run-out-of-scope', 'project');
        if (grant.workstreamIds && !grant.workstreamIds.includes(st.run.workstreamId ?? '')) {
            return no('run-out-of-scope', 'workstream');
        }
        if (d.kind === 'run.send')
            text = String(p.text ?? '');
    }
    if (utf8Len(text) > grant.limits.maxPromptBytes)
        return no('prompt-too-large');
    if (d.kind === 'run.start') {
        if (st.activeGrantRuns >= grant.limits.maxConcurrentRuns)
            return no('concurrency-limit');
        const hourAgo = st.now - 3_600_000;
        if (st.recentStarts.filter((t) => t > hourAgo).length >= grant.limits.maxStartsPerHour) {
            return no('rate-limited');
        }
    }
    return { ok: true, payload: parsed.data };
}
// Brain jobs use the `brain.triage` / `brain.retro` command kinds (commands.ts) — signed by the
// user's device like any command — and the `brain.result` frame (wire.ts).
// ---------------------------------------------------------------------------------------------
// Hygiene
// ---------------------------------------------------------------------------------------------
/** daemon → hub: a hygiene scan report (also accepted as the `hygiene.scan` command result). */
export const HygieneReportFrame = f('hygiene.report', {
    reportId: Id,
    projectId: z.string().optional(),
    repoPath: z.string().optional(),
    report: z.unknown(),
    ts: TimestampMs,
});
// ---------------------------------------------------------------------------------------------
// Daemon → hub `rpc` params (methods in wire.ts DAEMON_RPC_METHODS)
// ---------------------------------------------------------------------------------------------
/**
 * Scope: `runId` (a run on the calling machine) and/or `projectId` (a project the calling machine
 * is configured for). The hub rejects calls outside both.
 */
const Scope = { runId: Id.optional(), projectId: Id.optional() };
export const DAEMON_RPC_PARAMS = {
    /** → `{projectId, task: TaskSummary}` */
    'task.get': z.object({ ...Scope, taskId: Id.optional() }),
    /** → TaskSummary (MCP statuses) */
    'task.update': VikoTaskUpdateInput.extend(Scope),
    /** → `{taskId, status:'proposed'}` */
    'task.create': VikoTaskCreateInput.extend(Scope),
    /**
     * g18-batch → `{taskIds: {ref: id}}`. Tasks arrive in dependency order; `dependsOn` holds earlier
     * refs or existing task ids. Each task gets its own workstream and is `proposed`.
     */
    'task.createBatch': z.object({
        ...Scope,
        planId: z.string().min(1).max(128),
        rationale: z.string().max(2000).optional(),
        plan: VikoTaskCreateBatchInput.shape.plan,
        tasks: VikoTaskCreateBatchInput.shape.tasks,
    }),
    /** → `{projectId, project, workstream, currentTask?, tasks, rules, otherRuns}` */
    'workstream.get': z.object({ ...Scope, workstreamId: Id.optional() }),
    /** → `{handoffId}`; `brief` (daemon-composed) replaces the workstream brief when given. */
    handoff: VikoHandoffInput.extend({
        ...Scope,
        workstreamId: Id.optional(),
        brief: z.string().optional(),
    }),
    /** → `{results[]}` (project-scoped) */
    'history.search': VikoSearchHistoryInput.extend(Scope),
    /** → `{artifactId}` */
    'artifact.register': VikoPostArtifactInput.omit({ path: true }).extend({
        ...Scope,
        artifactId: Id,
        name: z.string(),
        mime: z.string().optional(),
        bytes: z.number().int().nonnegative().optional(),
        sha256: z.string().optional(),
        /** Additive: thumbnail artifact id, owning workstream (ArtifactWatcher). */
        thumbArtifactId: z.string().optional(),
        workstreamId: z.string().optional(),
    }),
    /** Cross-machine release request → `{status:'awaiting_approval'}` (a ship tap in the Inbox). */
    'release.dispatch': z.object({
        ...Scope,
        laneId: Id,
        machineId: Id.optional(),
        request: z.unknown().optional(),
    }),
    /** Signed GET for an attachment → `{url, name?, mime?, bytes?}` */
    'artifact.get': z.object({ ...Scope, artifactId: Id }),
    /** Imported / adopted session chunk → HistoryImportResult (history.ts). */
    'history.import': HistoryImportParams,
};
export const WORK_FRAMES = [
    WorkFrame,
    InboxRankedFrame,
    PolicyGrantFrame,
    PolicyRevokeFrame,
    PolicyRevokeAllFrame,
    HubDispatchFrame,
    HubDispatchResultFrame,
    HygieneReportFrame,
];
export const WorkWireFrame = z.discriminatedUnion('type', [...WORK_FRAMES]);
//# sourceMappingURL=work.js.map